import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
    Box,
    Alert,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Typography
} from '@mui/material';
import Video from 'twilio-video';
import { TWILIO_VIDEO_REGION } from '../../config';
import { attachVideoTrackInContainer } from '../../utils/twilioVideoDom';
import { twilioPublisherMediaOptions } from '../../utils/twilioVideoConnectOptions';

const LOG = '[PetLive][TwilioVideo][Publisher]';

/**
 * Transmissor: escolhe câmera e publica vídeo na sala.
 * options.name no Video.connect é o NOME DA SALA (GUID), não o identity do participante.
 */
export const TwilioVideoPublisher = ({ getToken, onDisconnected }) => {
    const previewRef = useRef(null);
    const roomRef = useRef(null);
    const localTrackRef = useRef(null);
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState('');
    const [loadingDevices, setLoadingDevices] = useState(true);
    const [connecting, setConnecting] = useState(false);
    const [live, setLive] = useState(false);
    const [error, setError] = useState('');

    const loadDevices = useCallback(async () => {
        setLoadingDevices(true);
        setError('');
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
            stream.getTracks().forEach((t) => t.stop());
            const all = await navigator.mediaDevices.enumerateDevices();
            const vids = all.filter((d) => d.kind === 'videoinput');
            setDevices(vids);
            setDeviceId((prev) => prev || (vids[0]?.deviceId ?? ''));
        } catch (e) {
            setError('Permissão da câmera necessária para listar dispositivos.');
        } finally {
            setLoadingDevices(false);
        }
    }, []);

    useEffect(() => {
        loadDevices();
    }, [loadDevices]);

    const stopLive = useCallback(() => {
        if (roomRef.current) {
            roomRef.current.disconnect();
            roomRef.current = null;
        }
        if (localTrackRef.current) {
            localTrackRef.current.stop();
            localTrackRef.current = null;
        }
        if (previewRef.current) {
            previewRef.current.innerHTML = '';
        }
        setLive(false);
        if (onDisconnected) {
            onDisconnected();
        }
    }, [onDisconnected]);

    useEffect(() => () => stopLive(), [stopLive]);

    const startLive = async () => {
        setError('');
        setConnecting(true);
        try {
            const result = await getToken();
            if (!result.success || !result.data?.token) {
                setError(result.message || 'Token indisponível');
                setConnecting(false);
                return;
            }

            const roomName = result.data.roomName;
            if (!roomName || typeof roomName !== 'string') {
                console.error(LOG, 'API não retornou roomName.');
                setError('Configuração da sala inválida (roomName).');
                setConnecting(false);
                return;
            }

            const track = await Video.createLocalVideoTrack(
                deviceId ? { deviceId: { exact: deviceId } } : {}
            );
            localTrackRef.current = track;

            attachVideoTrackInContainer(previewRef.current, track, LOG, { controls: true, muted: true });

            const connectOpts = {
                name: roomName,
                tracks: [track],
                audio: false,
                ...twilioPublisherMediaOptions
            };
            if (TWILIO_VIDEO_REGION) {
                connectOpts.region = TWILIO_VIDEO_REGION;
            }
            console.info(LOG, 'Video.connect', { roomName, region: connectOpts.region || 'default' });
            const room = await Video.connect(result.data.token, connectOpts);
            console.info(LOG, 'conectado', { roomSid: room?.sid, roomName: room?.name });
            roomRef.current = room;
            setLive(true);
        } catch (e) {
            console.error(LOG, 'falha', { code: e?.code, message: e?.message });
            if (e && e.code === 20151) {
                setError(
                    'Autorização do vídeo recusada (20151). No Twilio Console, crie ou use uma API Key na região United States (US1); chaves de outras regiões não servem para Programmable Video. Confira também se TWILIO_API_KEY_SECRET no .env corresponde exatamente ao secret dessa chave (SK).'
                );
            } else {
                setError(e.message || 'Falha ao iniciar transmissão');
            }
            stopLive();
        } finally {
            setConnecting(false);
        }
    };

    return (
        <Box>
            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 2 }} disabled={live || loadingDevices}>
                <InputLabel id="camera-select-label">Câmera</InputLabel>
                <Select
                    labelId="camera-select-label"
                    value={deviceId}
                    label="Câmera"
                    onChange={(e) => setDeviceId(e.target.value)}
                >
                    {devices.map((d) => (
                        <MenuItem key={d.deviceId} value={d.deviceId}>
                            {d.label || `Câmera ${d.deviceId.slice(0, 8)}…`}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <Box
                ref={previewRef}
                sx={{
                    width: '100%',
                    maxWidth: 640,
                    margin: '0 auto 16px',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#111',
                    minHeight: 200,
                    '& video': { width: '100%', display: 'block', objectFit: 'contain', maxHeight: '60vh' }
                }}
            />

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                Pré-visualização local. Ao iniciar, o vídeo é enviado para a sala do atendimento.
            </Typography>

            <Box display="flex" gap={2} flexWrap="wrap">
                {!live ? (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={startLive}
                        disabled={connecting || loadingDevices || devices.length === 0}
                    >
                        {connecting ? <CircularProgress size={22} color="inherit" /> : 'Iniciar transmissão'}
                    </Button>
                ) : (
                    <Button variant="outlined" color="error" onClick={stopLive}>
                        Encerrar transmissão
                    </Button>
                )}
                <Button variant="text" onClick={loadDevices} disabled={live || loadingDevices}>
                    Atualizar lista de câmeras
                </Button>
            </Box>
        </Box>
    );
};
